import {
  Box,
  Button,
  HStack,
  Input,
  RadioGroup,
  Stack,
  Text,
  Textarea,
} from 'tcm-chakra-ui'
import React, { useEffect, useState } from 'react'
import {
  OTHER_WRAP_CODE,
  PROVIDER_NOT_FOUND_WRAP_CODE,
  WRAP_CODES,
  getWrapCodeContext,
} from '../../features/tcm-workflow/wrapCodes'

type WrapCodeModalProps = {
  open: boolean
  isPac: boolean
  onCancel: () => void
  onSubmit: (
    scheduledOutside: boolean,
    wrapCode: string,
    additionalDetails: string | undefined,
  ) => void
}

const radioControlCss = {
  width: '1rem',
  height: '1rem',
  borderRadius: 'full',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'border.gray',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  _checked: {
    borderColor: 'background.brandDark',
    bg: 'background.brandDark',
  },
} as const

const radioIndicatorCss = {
  width: '0.375rem',
  height: '0.375rem',
  borderRadius: 'full',
  bg: 'background.primary',
} as const

export function WrapCodeModal({
  open,
  isPac,
  onCancel,
  onSubmit,
}: WrapCodeModalProps) {
  const [scheduledOutside, setScheduledOutside] = useState<boolean | null>(null)
  const [selectedCode, setSelectedCode] = useState('')
  const [otherText, setOtherText] = useState('')

  useEffect(() => {
    if (open) {
      setScheduledOutside(null)
      setSelectedCode('')
      setOtherText('')
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onCancel])

  if (!open) return null

  const isOtherSelected = selectedCode === OTHER_WRAP_CODE
  const isProviderNotFoundSelected = selectedCode === PROVIDER_NOT_FOUND_WRAP_CODE
  const needsAdditionalText = isOtherSelected || isProviderNotFoundSelected
  const canSubmit =
    scheduledOutside !== null &&
    (isOtherSelected
      ? otherText.trim().length >= 3
      : isProviderNotFoundSelected
        ? otherText.trim().length >= 1
        : selectedCode !== '')

  const context =
    scheduledOutside !== null
      ? getWrapCodeContext(scheduledOutside, isPac)
      : null
  const options = context ? WRAP_CODES[context] : null
  const wrapCodeTitle =
    scheduledOutside === true
      ? 'How did you schedule the follow-up?'
      : 'Why was a follow-up not scheduled?'

  const handleSubmit = () => {
    if (scheduledOutside === null) return
    onSubmit(
      scheduledOutside,
      selectedCode,
      needsAdditionalText ? otherText.trim() : undefined,
    )
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="rgba(15, 23, 42, 0.45)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={1600}
      px={4}
      role="dialog"
      aria-modal="true"
      aria-label="Encounter outcome"
      onClick={onCancel}
    >
      <Box
        w="100%"
        maxW="32rem"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="border.gray"
        bg="background.primary"
        boxShadow="2xl"
        p={5}
        onClick={(event) => event.stopPropagation()}
      >
        <Stack gap={4}>
          {/* Yes / No */}
          <Stack gap={2}>
            <Text id="wrap-code-scheduled-label" color="text.brandDark" fontSize="lg" fontWeight="700">
              Did you schedule a follow-up for this patient outside of this tool?
            </Text>
            <RadioGroup.Root
              aria-labelledby="wrap-code-scheduled-label"
              value={scheduledOutside === null ? '' : scheduledOutside ? 'yes' : 'no'}
              onValueChange={({ value }) => {
                setScheduledOutside(value === 'yes')
                setSelectedCode('')
                setOtherText('')
              }}
            >
              <HStack gap={6}>
                {(['no', 'yes'] as const).map((val) => (
                  <RadioGroup.Item key={val} value={val}>
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemControl css={radioControlCss}>
                      <RadioGroup.ItemIndicator css={radioIndicatorCss} />
                    </RadioGroup.ItemControl>
                    <RadioGroup.ItemText>
                      {val === 'yes' ? 'Yes' : 'No'}
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                ))}
              </HStack>
            </RadioGroup.Root>
          </Stack>

          {/* Wrap code list — revealed once Yes/No is answered */}
          {options && (
            <Stack gap={2}>
              <Text id="wrap-code-options-label" color="text.secondary" fontSize="sm" fontWeight="600">
                {wrapCodeTitle}
              </Text>
              <RadioGroup.Root
                aria-labelledby="wrap-code-options-label"
                value={selectedCode}
                onValueChange={({ value }) => {
                  setSelectedCode(value ?? '')
                  setOtherText('')
                }}
              >
                <Stack gap={2}>
                  {options.map((option) => (
                    <React.Fragment key={option}>
                      <RadioGroup.Item value={option}>
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemControl css={radioControlCss}>
                          <RadioGroup.ItemIndicator css={radioIndicatorCss} />
                        </RadioGroup.ItemControl>
                        <RadioGroup.ItemText>{option}</RadioGroup.ItemText>
                      </RadioGroup.Item>
                      {option === PROVIDER_NOT_FOUND_WRAP_CODE && isProviderNotFoundSelected && (
                        <Input
                          placeholder="Provider name"
                          value={otherText}
                          onChange={(e) => setOtherText(e.target.value)}
                          bg="background.primary"
                          border="1px solid"
                          borderColor="border.gray"
                          px={3}
                          aria-label="Provider name"
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Stack>
              </RadioGroup.Root>
              {isOtherSelected && (
                <Textarea
                  placeholder="Please describe…"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  rows={3}
                  bg="background.primary"
                  border="1px solid"
                  borderColor="border.gray"
                  px={3}
                  py={2}
                  aria-label="Additional details"
                />
              )}
            </Stack>
          )}

          <HStack justify="flex-end" gap={3}>
            <Button
              variant="outline"
              css={{
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.gray',
                borderRadius: 'md',
                px: 5,
                minH: 10,
                fontWeight: 600,
                backgroundColor: 'background.primary',
              }}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              disabled={!canSubmit}
              css={{
                bg: 'background.brandDark',
                color: 'text.white',
                px: 5,
                minH: 10,
                borderRadius: 'md',
                fontWeight: 700,
              }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </HStack>
        </Stack>
      </Box>
    </Box>
  )
}
